import { AppContext } from "helpers/context";
import { useContext, useState, useEffect } from "react";
import { Button, FormControl, InputLabel, TextField, Popover, Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { NumericFormat } from 'react-number-format';
import dayjs from "dayjs";
import "dayjs/locale/en"; // 导入中文语言包
import "dayjs/locale/zh"; // 导入中文语言包

interface Props {
    width?: string;
    data: any;
    onChange: (value: any) => void;
}

const FilterMenu = (props: Props) => {
    const [t] = useTranslation();
    const [context, dispatch] = useContext(AppContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [filterData, setFilterData] = useState<any>(undefined);
    const [errors, setErrors] = useState<any>({})

    const MAX_VALUE = 1000000000000000;
    const validateInteger = ({ name, value, }: {
        name: string;
        value: string;
    }) => {
        if (value.trim() === "") return undefined;
        const num = Number(value);
        if (isNaN(num)) return t("locations.add.isNaN", { key: t("locationSet.filterModal." + name) });
        if (num < 0) return t("locations.add.less0", { key: t("locationSet.filterModal." + name) });
        if (num > MAX_VALUE) return t("locations.add.less9Q", { key: t("locationSet.filterModal." + name) });
        return undefined;
    }

    const updateError = (key: string, msg?: string) => {
        setErrors((prev: any) => ({ ...prev, [key]: msg }));
    };

    const validateMinMaxRelation = ({ name, min, max }: { name: string; min: string | number; max: string | number }) => {
        const numMin = Number(min);
        const numMax = Number(max);
        if (name === "min" && numMax) {
            if (!isNaN(numMax) && numMin && numMin > numMax) {
                return updateError("min", t("locations.add.moreMax"));
            }
            updateError("min");
            if (errors["max"] === t("locations.add.lessMin")) {
                updateError("max");
            }
        }

        if (name === "max" && numMin) {
            if (!isNaN(numMin) && numMax && numMax < numMin) {
                return updateError("max", t("locations.add.lessMin"));
            }
            updateError("max");
            if (errors["min"] === t("locations.add.moreMax")) {
                updateError("min");
            }
        }
    };


    const handleChange = (event: any) => {
        const { name, value } = event.target;
        let newData = {
            ...filterData,
            [name]: value,
        };

        if (filterData.type === "number") {
            const validateMsg = validateInteger({ name, value });
            updateError(name, validateMsg);
            if (!validateMsg) {
                // 校验 min-max 关系
                validateMinMaxRelation({ ...newData, name });
            }
        } else if (filterData.type === "time") {
            // newData[name] = value.valueOf()
            newData.min && (newData.min = newData.min.valueOf());
            newData.max && (newData.max = newData.max.valueOf());
        }

        props.onChange(JSON.parse(JSON.stringify(newData)));
    }

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleReset = () => {
        const resetValue = filterData.type === "number" ? "" : null;
        const newData = {
            ...filterData,
            min: resetValue,
            max: resetValue,
        };
        setFilterData(newData);
        props.onChange(JSON.parse(JSON.stringify(newData)));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        let propsData = JSON.parse(JSON.stringify(props.data));
        if (propsData.type === "time") {
            if (propsData.min) propsData.min = dayjs(propsData.min)
            if (propsData.max) propsData.max = dayjs(propsData.max)
        }
        setFilterData(propsData)
    }, [props.data])

    return filterData && (<FormControl fullWidth sx={{ width: props.width }} key={filterData.title}>
        <InputLabel shrink={false}
            sx={{
                color: open ? 'success' : '#666',
            }}
        >{t("locationSet.filterModal." + filterData.title)}</InputLabel>
        <TextField
            color="success"
            fullWidth
            onClick={handleClick}
            placeholder=""
            InputProps={{
                readOnly: true,
                endAdornment: (
                    <span
                        className="material-icons"
                        style={{
                            color: open ? 'success' : '#999',
                            pointerEvents: 'none'
                        }}
                    >
                        arrow_drop_down
                    </ span>
                )

            }}
        />

        <Popover
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
        >
            <div style={{ padding: "8px", borderRadius: "4px" }}>
                <div style={{ margin: "10px 0" }}>
                    <div style={{
                        margin: "5px 0 10px",
                        fontStyle: "normal",
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: "#000000de",
                        fontWeight: 500
                    }}>{t("locationSet.filterModal." + filterData.title)}</div>
                    <Stack spacing={1} direction="row">
                        {
                            filterData.type === "number" ?
                                <>
                                    <NumericFormat
                                        customInput={TextField}
                                        name="min"
                                        thousandSeparator=","
                                        decimalSeparator="."
                                        allowNegative={false}
                                        type="text"
                                        inputMode="decimal"
                                        label={t("locationSet.filterModal.min")}
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        sx={{
                                            width: "130px",
                                        }}
                                        value={filterData.min}
                                        error={Boolean(errors.min)}
                                        helperText={errors.min}
                                        onValueChange={(values) => handleChange({ target: { name: 'min', value: values.value } })}
                                    />
                                    <NumericFormat
                                        customInput={TextField}
                                        name="max"
                                        thousandSeparator=","
                                        decimalSeparator="."
                                        allowNegative={false}
                                        type="text"
                                        inputMode="decimal"
                                        label={t("locationSet.filterModal.max")}
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        sx={{
                                            width: "130px",
                                        }}
                                        value={filterData.max}
                                        error={Boolean(errors.max)}
                                        helperText={errors.max}
                                        onValueChange={(values) => handleChange({ target: { name: 'max', value: values.value } })}
                                    />
                                </>
                                :
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale={context.language === "cn" ? "zh" : "en"}
                                >
                                    <DatePicker
                                        label={t("locationSet.filterModal.startDate")}
                                        format="YYYY-MM-DD"
                                        onChange={(val) => handleChange({ target: { name: 'min', value: val } })}
                                        value={filterData.min}
                                        maxDate={filterData.max}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                color: "success",
                                                sx: {
                                                    width: "180px",
                                                }
                                            },
                                        }}
                                    />
                                    <DatePicker
                                        label={t("locationSet.filterModal.endDate")}
                                        format="YYYY-MM-DD"
                                        onChange={(val) => handleChange({ target: { name: 'max', value: val } })}
                                        value={filterData.max}
                                        minDate={filterData.min}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                color: "success",
                                                sx: {
                                                    width: "180px",
                                                }
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                        }
                    </Stack>
                </div>
                <Box mt={2}>
                    <Button
                        color="success"
                        disabled={!(filterData.min || filterData.max)}
                        onClick={handleReset}
                    >
                        {t("buttons.reset")}
                    </Button>
                </Box>
            </div>
        </Popover>
    </FormControl>);
};

export default FilterMenu;
